import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { supabaseClient } from '../config/supabase';
import { VERSION } from '../config/version';
import AuthRecoveryModal from './shared/AuthRecoveryModal';
import { markWelcomePending, pageTitle, webVersionLabel, photoBackdropLikely, usesPhotoBackdrop } from '../lib/signInBranding';
import { doorForAddress, ENABLE_SIGN_IN_SSO } from '../lib/signInDoors';
import useSignInBrand from './auth/useSignInBrand';
import { skylineSvg } from './auth/skyline';
import atlantaBackdrop from '../assets/signin-bg-atlanta.jpg';
import rideLogo from '../assets/the-ride-atl-logo.png';
import RideLogoFloat from './brand/RideLogoFloat';
import './auth/signin.css';

function GoogleLogo() {
  return (
    <svg aria-hidden="true" focusable="false" viewBox="0 0 24 24">
      <path fill="#4285F4" d="M23.49 12.27c0-.82-.07-1.6-.19-2.36H12v4.46h6.46c-.28 1.5-1.12 2.77-2.39 3.62v2.96h3.87c2.27-2.09 3.55-5.16 3.55-8.68Z" />
      <path fill="#34A853" d="M12 24c3.24 0 5.95-1.07 7.94-2.91l-3.87-2.96c-1.07.72-2.44 1.14-4.07 1.14-3.13 0-5.78-2.11-6.73-4.95H1.28v3.05C3.26 21.3 7.32 24 12 24Z" />
      <path fill="#FBBC05" d="M5.27 14.32A7.21 7.21 0 0 1 4.89 12c0-.8.14-1.58.38-2.32V6.63H1.28A11.96 11.96 0 0 0 0 12c0 1.93.46 3.76 1.28 5.37l3.99-3.05Z" />
      <path fill="#EA4335" d="M12 4.73c1.76 0 3.34.6 4.58 1.79l3.44-3.44C17.94 1.14 15.23 0 12 0 7.32 0 3.26 2.7 1.28 6.63l3.99 3.05C6.22 6.84 8.87 4.73 12 4.73Z" />
    </svg>
  );
}

function MicrosoftLogo() {
  return (
    <svg aria-hidden="true" focusable="false" viewBox="0 0 24 24">
      <path fill="#F25022" d="M1 1h10v10H1z" />
      <path fill="#7FBA00" d="M13 1h10v10H13z" />
      <path fill="#00A4EF" d="M1 13h10v10H1z" />
      <path fill="#FFB900" d="M13 13h10v10H13z" />
    </svg>
  );
}

function AppleLogo() {
  return (
    <svg aria-hidden="true" focusable="false" viewBox="0 0 24 24">
      <path fill="currentColor" d="M17.05 12.47c-.03-2.78 2.27-4.12 2.38-4.19-1.3-1.9-3.31-2.16-4.01-2.19-1.69-.18-3.33 1.01-4.19 1.01-.88 0-2.2-.99-3.63-.96-1.85.03-3.58 1.1-4.53 2.78-1.96 3.4-.5 8.4 1.38 11.15.94 1.35 2.04 2.86 3.48 2.8 1.41-.06 1.94-.9 3.64-.9 1.69 0 2.19.9 3.66.87 1.52-.03 2.48-1.35 3.38-2.71 1.09-1.55 1.52-3.08 1.54-3.16-.03-.01-2.97-1.14-3-4.5ZM14.32 4.3c.76-.95 1.28-2.24 1.13-3.55-1.1.05-2.48.76-3.27 1.68-.7.81-1.33 2.16-1.16 3.42 1.24.09 2.5-.63 3.3-1.55Z" />
    </svg>
  );
}

function EyeIcon({ open }) {
  return (
    <svg aria-hidden="true" focusable="false" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12Z" />
      <circle cx="12" cy="12" r="3" />
      {!open && <path d="M4 20 20 4" />}
    </svg>
  );
}

// BL-407 footer: "Powered by smarTEKnowledgy · v<version>", the version read live and never hard-coded.
export function SignInFooter() {
  return (
    <footer className="ia-signin__foot">
      {/* rel="noopener" per the locked directive: no window.opener access, but the referral still reaches smarteknowledgy.com. */}
      {/* eslint-disable-next-line react/jsx-no-target-blank */}
      <span>Powered by <a className="ia-signin__stk" href="https://www.smarteknowledgy.com" target="_blank" rel="noopener">smarTEKnowledgy</a> <span className="ia-signin__version">· v{webVersionLabel(VERSION)}</span></span>
    </footer>
  );
}

// The IntelAgent door's background: the locked BL-360 scene (core hub, spokes, light pulses, glass panels). It is
// decorative (aria-hidden, inert, no data) and loaded after the card so it never delays sign-in.
const IntelAgentScene = lazy(() => import('./auth/IntelAgentScene'));

// Platform door: "IntelAgent" with CENTRAL COMMAND beneath. Tenant door: the real logo above the card (the name stays for
// screen readers and shows as text only while the logo is missing or unloaded).
function BrandHeader({ brand, door }) {
  const [failedLogo, setFailedLogo] = useState(null);
  const heading = door.identity.signInHeading || brand.brand_name;
  if (door.slug === null) {
    const [main, ...rest] = heading.split(' ');
    return (
      <div className="ia-signin__brand">
        <h1 className="ia-signin__title ia-signin__title--platform">
          <span className="ia-signin__title-main">{main}</span>{' '}
          <span className="ia-signin__title-sub">{rest.join(' ')}</span>
        </h1>
      </div>
    );
  }
  const logo = door.signInLogo ? `${process.env.PUBLIC_URL || ''}/${door.signInLogo}` : (brand.logo_url || rideLogo);
  const showLogo = failedLogo !== logo;
  return (
    <div className="ia-signin__brand">
      {showLogo && (door.slug === 'the-ride-atl'
        // The Ride's moving logo (locked 2026-10-25): glass disc, no chrome ring, rising and settling. Other tenants keep a still image.
        ? <RideLogoFloat className="ia-signin__logo-float" imgClassName="ia-signin__logo ia-signin__logo--door" src={logo} alt={`${brand.brand_name} logo`} onError={() => setFailedLogo(logo)} />
        : <img className="ia-signin__logo ia-signin__logo--door" src={logo} alt={`${brand.brand_name} logo`} onError={() => setFailedLogo(logo)} />)}
      <h1 className={showLogo ? 'ia-sr-only' : 'ia-signin__title'}>{heading}</h1>
    </div>
  );
}

function SignInMessage({ message }) {
  const ref = useRef(null);
  // A failed sign-in must be seen on every door and screen size: bring the message into view when it appears.
  useEffect(() => {
    if (message && ref.current?.scrollIntoView) ref.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [message]);
  if (!message) return null;
  return (
    typeof message === 'string'
      ? <p ref={ref} className="ia-signin__message" role="alert">{message}</p>
      : (
        <div ref={ref} className="ia-signin__message ia-signin__message--denied">
          <h2 className="ia-signin__denied-title" role="alert">{message.heading}</h2>
          <p>{message.text}</p>
          <p>{message.intro}</p>
          <a className="ia-signin__door-link" href={message.href}>{message.linkText}</a>
        </div>
      )
  );
}

function LoginScreen({
  onLogin,
  onSignup,
  mode = 'login',
  setMode,
  loading = false,
  message = '',
  isPlatformStaff = false,
  onStaffProvision = null,
  staffProvisionLoading = false,
  staffProvisionMessage = '',
  leaving = false
}) {
  const [orgName, setOrgName] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [website, setWebsite] = useState('');
  const signupStartRef = useRef(Date.now());
  const [staffOrgName, setStaffOrgName] = useState('');
  const [staffInviteEmail, setStaffInviteEmail] = useState('');
  const [showContactAdmin, setShowContactAdmin] = useState(false);
  const [showRecovery, setShowRecovery] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const brand = useSignInBrand();
  const door = doorForAddress(window.location);
  const photo = usesPhotoBackdrop(brand);
  const preloadPhoto = photoBackdropLikely(brand);
  useEffect(() => {
    const previous = document.title;
    document.title = pageTitle('Sign In', door.slug ? door.identity.name : door.identity.signInHeading);
    return () => { document.title = previous; };
  }, [door]);
  // BL-402.1: preload the Atlanta photo on the sign-in route so it's there when The Ride's card shows.
  useEffect(() => {
    if (!preloadPhoto || document.querySelector('link[data-ia-signin-photo]')) return;
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = atlantaBackdrop;
    link.setAttribute('data-ia-signin-photo', '');
    document.head.appendChild(link);
  }, [preloadPhoto]);
  useEffect(() => {
    if (mode === 'signup') {
      signupStartRef.current = Date.now();
      setWebsite('');
    }
    if (mode !== 'no-tenant') {
      setShowContactAdmin(false);
    }
  }, [mode]);

  const handleLogin = (e) => {
    e.preventDefault();
    onLogin(email, password);
  };

  const handleSignup = (e) => {
    e.preventDefault();
    const elapsedMs = Date.now() - signupStartRef.current;
    onSignup({ orgName, ownerName, email, password, website, elapsedMs });
  };

  const handleStaffProvision = (e) => {
    e.preventDefault();
    onStaffProvision?.({ orgName: staffOrgName, email: staffInviteEmail });
  };

  const handleMicrosoftSignIn = async () => {
    const { error } = await supabaseClient.auth.signInWithOAuth({
      provider: 'azure',
      options: {
        scopes: 'email',
        redirectTo: window.location.origin,
        queryParams: {
          prompt: 'select_account'
        }
      }
    });
    if (error) alert('Microsoft sign-in failed: ' + error.message);
  };

  const handleGoogleSignIn = async () => {
    const redirectTo = window.location.origin;
    const { error } = await supabaseClient.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo }
    });
    if (error) alert('Google sign-in failed: ' + error.message);
  };

  const handleAppleSignIn = async () => {
    const { error } = await supabaseClient.auth.signInWithOAuth({ provider: 'apple' });
    if (error) alert('Apple sign-in failed: ' + error.message);
  };

  // BL-402: the tile only arms the welcome sequence for the OAuth landing; the handlers above are unchanged.
  const sso = (handler) => () => {
    markWelcomePending();
    handler();
  };

  const subtitle = mode === 'signup' ? 'Create account' : '';

  return (
    <div className={`ia-signin${photo ? ' ia-signin--photo' : ''}${!photo && door.slug === null ? ' ia-signin--platform' : ''}${leaving ? ' is-leaving' : ''}`}
      style={photo ? { '--ia-photo': `url(${atlantaBackdrop})` } : { '--ia-sky': skylineSvg() }}>
      {!photo && door.slug === null && <Suspense fallback={null}><IntelAgentScene /></Suspense>}
      <div className="ia-signin__stack">
      <BrandHeader brand={brand} door={door} />
      <main className="ia-signin__card" aria-label="Sign In">
        {subtitle && <p className="ia-signin__subtitle">{subtitle}</p>}
        {isPlatformStaff && onStaffProvision && (
          <form onSubmit={handleStaffProvision} className="ia-signin__panel">
            <p className="ia-signin__panel-title">Staff-provisioned tenant invite</p>
            <label className="ia-signin__field">Organization name
              <input type="text" value={staffOrgName} onChange={(e) => setStaffOrgName(e.target.value)} className="ia-signin__input" required />
            </label>
            <label className="ia-signin__field">Invite email
              <input type="email" value={staffInviteEmail} onChange={(e) => setStaffInviteEmail(e.target.value)} className="ia-signin__input" required />
            </label>
            <button type="submit" disabled={staffProvisionLoading} className="ia-signin__primary">
              {staffProvisionLoading ? 'Working...' : 'Send invite'}
            </button>
            {staffProvisionMessage && <p className="ia-signin__help">{staffProvisionMessage}</p>}
          </form>
        )}
        {mode === 'no-tenant' ? (
          <div className="ia-signin__form">
            <SignInMessage message={message} />
            <button type="button" onClick={() => setMode('signup')} className="ia-signin__primary">Create a New Organization</button>
            <button type="button" onClick={() => setShowContactAdmin(true)} className="ia-signin__secondary">I Was Invited - Contact Your Admin</button>
            {showContactAdmin && (
              <p className="ia-signin__help">Ask your organization's admin to send you an invite, then use the link in that invite email to sign in.</p>
            )}
            <div className="ia-signin__links">
              <button type="button" onClick={() => setMode('login')} className="ia-signin__link">Back to sign in</button>
            </div>
          </div>
        ) : (
          <>
            <form onSubmit={mode === 'signup' ? handleSignup : handleLogin} className="ia-signin__form">
              {mode === 'signup' && (
                <>
                  <label className="ia-signin__field">Organization name
                    <input type="text" value={orgName} onChange={(e) => setOrgName(e.target.value)} className="ia-signin__input" required />
                  </label>
                  <label className="ia-signin__field">Owner name
                    <input type="text" value={ownerName} onChange={(e) => setOwnerName(e.target.value)} className="ia-signin__input" required />
                  </label>
                  <div className="ia-sr-only" aria-hidden="true">
                    <label htmlFor="website">Website</label>
                    <input
                      id="website"
                      name="website"
                      type="text"
                      autoComplete="off"
                      tabIndex={-1}
                      value={website}
                      onChange={(e) => setWebsite(e.target.value)}
                    />
                  </div>
                </>
              )}
              <label className="ia-signin__field">Email
                <input aria-label="Email" name="username" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} className="ia-signin__input" required />
              </label>
              <label className="ia-signin__field">Password
                <span className="ia-signin__password">
                  <input key={mode} aria-label="Password" name="password" type={showPassword ? 'text' : 'password'} autoComplete={mode === 'signup' ? 'new-password' : 'current-password'} value={password} onChange={(e) => setPassword(e.target.value)} className="ia-signin__input" required />
                  <button type="button" className="ia-signin__reveal" aria-pressed={showPassword} aria-label={showPassword ? 'Hide password' : 'Show password'} title={showPassword ? 'Hide password' : 'Show password'} onClick={() => setShowPassword(!showPassword)}>
                    <EyeIcon open={showPassword} />
                  </button>
                </span>
              </label>
              <SignInMessage message={message} />
              <button type="submit" disabled={loading} className="ia-signin__primary">
                {loading ? 'Working...' : mode === 'signup' ? 'Create account' : 'Sign In'}
              </button>
            </form>
            {ENABLE_SIGN_IN_SSO && (
              <>
                <div className="ia-signin__or">or continue with</div>
                <div className="ia-signin__sso">
                  <button type="button" onClick={sso(handleGoogleSignIn)} disabled={loading} aria-label="Continue with Google" title="Continue with Google"><GoogleLogo /></button>
                  <button type="button" onClick={sso(handleMicrosoftSignIn)} disabled={loading} aria-label="Continue with Microsoft" title="Continue with Microsoft"><MicrosoftLogo /></button>
                  <button type="button" onClick={sso(handleAppleSignIn)} disabled={loading} aria-label="Continue with Apple" title="Continue with Apple"><AppleLogo /></button>
                </div>
              </>
            )}
            <div className="ia-signin__links">
              {mode === 'login' && (
                <button type="button" onClick={() => setShowRecovery(true)} className="ia-signin__link">
                  Can't sign in / Forgot password?
                </button>
              )}
              {door.slug === null && (
                <button type="button" onClick={() => setMode(mode === 'signup' ? 'login' : 'signup')} className="ia-signin__link ia-signin__link--quiet">
                  {mode === 'signup' ? 'Already have an account? Sign in' : 'Sign up / Create a new tenant?'}
                </button>
              )}
            </div>
          </>
        )}
      </main>
      <SignInFooter />
      </div>
      {showRecovery && <AuthRecoveryModal onClose={() => setShowRecovery(false)} />}
    </div>
  );
}

export default LoginScreen;
